import React from "react";
import { BookOpen, Scale } from "lucide-react";
import type { ContentSection } from "@/content/types";

// Cover art for a blog or comparison page, drawn rather than uploaded so a
// new page needs no image: the brand gradient, a waveform whose shape comes
// from the slug (stable per page, different between pages) and the section's
// icon. Decorative, so hidden from screen readers.

const hash = (s: string) =>
  [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

function bars(seed: string, n: number) {
  let h = hash(seed);
  return Array.from({ length: n }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    // A rise and fall across the strip, jittered per bar.
    const envelope = Math.sin((Math.PI * (i + 0.5)) / n);
    return 0.18 + 0.82 * envelope * (0.45 + ((h >>> 8) % 1000) / 1800);
  });
}

const THEMES = {
  blog: {
    bg: "tw-bg-[radial-gradient(120%_120%_at_0%_0%,#782DC8_0%,#4A2380_45%,#2E0F52_100%)]",
    bar: "tw-bg-jam-lime",
    icon: BookOpen,
  },
  compare: {
    bg: "tw-bg-[radial-gradient(120%_120%_at_100%_0%,#D1FE17_0%,#9BD80F_30%,#4A2380_75%,#2E0F52_100%)]",
    bar: "tw-bg-white",
    icon: Scale,
  },
};

interface Props {
  section: ContentSection;
  slug: string;
  kind: string;
  size?: "hero" | "card";
  className?: string;
}

export default function CoverArt({
  section,
  slug,
  kind,
  size = "card",
  className = "",
}: Props) {
  const theme = THEMES[section];
  const Icon = theme.icon;
  const hero = size === "hero";
  const heights = bars(slug, hero ? 36 : 24);
  return (
    <div
      aria-hidden="true"
      className={`tw-relative tw-flex tw-flex-col tw-justify-between tw-overflow-hidden ${theme.bg} ${
        hero
          ? "tw-min-h-[190px] tw-rounded-[26px] tw-p-5 md:tw-min-h-[340px] md:tw-rounded-[30px] md:tw-p-8"
          : "tw-h-[150px] tw-rounded-[22px] tw-p-4"
      } ${className}`}
    >
      <div className="tw-flex tw-items-center tw-justify-between">
        <span
          className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/90 tw-font-extrabold tw-text-jam-ink ${
            hero
              ? "tw-px-3.5 tw-py-1.5 tw-text-sm"
              : "tw-px-2.5 tw-py-1 tw-text-xs"
          }`}
        >
          <Icon size={hero ? 16 : 13} strokeWidth={2.6} />
          {kind}
        </span>
        <span
          className={`jam-display tw-font-extrabold tw-text-white/80 ${
            hero ? "tw-text-lg" : "tw-text-sm"
          }`}
        >
          Jammable
        </span>
      </div>
      <div
        className={`tw-flex tw-items-end ${hero ? "tw-h-24 tw-gap-[4px] md:tw-h-48 md:tw-gap-[5px]" : "tw-h-16 tw-gap-[3px]"}`}
      >
        {heights.map((v, i) => (
          <span
            key={i}
            className={`tw-flex-1 tw-rounded-full ${theme.bar}`}
            style={{
              height: `${Math.round(v * 100)}%`,
              opacity: 0.55 + v * 0.45,
            }}
          />
        ))}
      </div>
    </div>
  );
}
